"use client"

import { closestCenter, DndContext, type DragEndEvent, DragOverlay, useDraggable, useDroppable } from "@dnd-kit/core"
import { cva, type VariantProps } from "class-variance-authority"
import * as React from "react"
import { cn } from "../../lib/utils"
import type { DisplayGap, DisplayGapMatchInteraction, DisplayGapText } from "../../shared/types"
import { HTMLContent } from "../shared/html-content"

// ============================================================================
// Safe Inline HTML - For rendering sanitized content
// ============================================================================

/**
 * Renders pre-sanitized HTML content inline.
 * Content is sanitized server-side before being sent to the client.
 */
function SafeInlineHTML({ html, className }: { html: string; className?: string }) {
	const ref = React.useRef<HTMLSpanElement>(null)

	React.useLayoutEffect(() => {
		if (ref.current) {
			ref.current.innerHTML = html
		}
	}, [html])

	return <span ref={ref} className={className} />
}

// ============================================================================
// State Helpers
// ============================================================================

/** Internal state: Map<gapId, sourceId> */
type GapMatchState = Record<string, string>

/** Convert internal state to QTI response format: ["sourceId gapId", ...] */
function toQtiResponse(state: GapMatchState): string[] {
	return Object.entries(state).map(([gapId, sourceId]) => `${sourceId} ${gapId}`)
}

/** Convert QTI response format to internal state */
function fromQtiResponse(response: string[]): GapMatchState {
	const state: GapMatchState = {}
	for (const pair of response) {
		const parts = pair.split(" ")
		const sourceId = parts[0]
		const gapId = parts[1]
		if (sourceId && gapId) {
			state[gapId] = sourceId
		}
	}
	return state
}

// ============================================================================
// Palette Variants
// ============================================================================

const gapSlotVariants = cva(
	[
		"inline-flex items-center justify-center",
		"min-w-[4rem] min-h-[1.75rem] px-2 mx-1 my-1",
		"border rounded-xs align-middle",
		"transition-all duration-150"
	],
	{
		variants: {
			state: {
				empty: "border-muted-foreground/30 bg-muted/20",
				hover: "border-macaw bg-macaw/10",
				filled: "border-macaw bg-macaw/10",
				correct: "border-owl bg-owl/15",
				incorrect: "border-cardinal bg-cardinal/15"
			}
		},
		defaultVariants: {
			state: "empty"
		}
	}
)

const sourceTokenVariants = cva(
	[
		"inline-flex items-center justify-center",
		"h-7 px-2 rounded-xs border-2",
		"text-sm font-semibold",
		"transition-all duration-150",
		"select-none"
	],
	{
		variants: {
			state: {
				idle: "bg-background border-border shadow-[0_2px_0_var(--color-border)] cursor-grab hover:border-macaw hover:shadow-[0_2px_0_var(--color-macaw)]",
				selected: "bg-macaw/15 border-macaw shadow-[0_2px_0_var(--color-whale)] ring-2 ring-macaw/30",
				dragging: "opacity-50 cursor-grabbing",
				disabled: "opacity-50 cursor-not-allowed bg-muted/50 border-muted shadow-none"
			}
		},
		defaultVariants: {
			state: "idle"
		}
	}
)

// ============================================================================
// Draggable Source Token
// ============================================================================

interface SourceTokenProps {
	id: string
	contentHtml: string
	disabled: boolean
	isSelected: boolean
	onClick: () => void
	remainingUses: number
}

function SourceToken({ id, contentHtml, disabled, isSelected, onClick, remainingUses }: SourceTokenProps) {
	const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
		id: `source-${id}`,
		data: { type: "source", sourceId: id },
		disabled: disabled || remainingUses <= 0
	})

	if (remainingUses <= 0) return null

	// Don't apply transform here - the DragOverlay handles the visual that follows the cursor.
	// The original element stays in place and fades out when dragging.

	const getState = (): "idle" | "selected" | "dragging" | "disabled" => {
		if (disabled) return "disabled"
		if (isDragging) return "dragging"
		if (isSelected) return "selected"
		return "idle"
	}

	return (
		<button
			ref={setNodeRef}
			type="button"
			onClick={disabled ? undefined : onClick}
			className={cn(sourceTokenVariants({ state: getState() }), "active:cursor-grabbing")}
			disabled={disabled}
			{...attributes}
			{...listeners}
		>
			<SafeInlineHTML html={contentHtml} />
			{remainingUses > 1 && <span className="ml-1 text-xs text-muted-foreground">×{remainingUses}</span>}
		</button>
	)
}

// ============================================================================
// Droppable Gap Slot
// ============================================================================

interface GapSlotProps {
	gapId: string
	filledWith: DisplayGapText | undefined
	onClear: () => void
	onClick: () => void
	disabled: boolean
	isCorrect?: boolean
	hasSubmitted: boolean
}

function GapSlot({ gapId, filledWith, onClear, onClick, disabled, isCorrect, hasSubmitted }: GapSlotProps) {
	const { setNodeRef, isOver } = useDroppable({
		id: `gap-${gapId}`,
		data: { type: "gap", gapId },
		disabled
	})

	const getState = (): VariantProps<typeof gapSlotVariants>["state"] => {
		if (hasSubmitted && filledWith) {
			return isCorrect ? "correct" : "incorrect"
		}
		if (isOver) return "hover"
		if (filledWith) return "filled"
		return "empty"
	}

	return (
		<span ref={setNodeRef} className={cn(gapSlotVariants({ state: getState() }))}>
			{filledWith ? (
				<button
					type="button"
					onClick={disabled ? undefined : onClear}
					disabled={disabled}
					className={cn(
						"flex items-center gap-1",
						!disabled && "hover:text-cardinal cursor-pointer",
						disabled && "cursor-default"
					)}
				>
					<SafeInlineHTML html={filledWith.contentHtml} />
					{!disabled && <span className="text-xs opacity-60">×</span>}
				</button>
			) : (
				<button
					type="button"
					onClick={disabled ? undefined : onClick}
					disabled={disabled}
					className={cn(
						"w-full h-full min-h-[1.5em] flex items-center justify-center",
						!disabled && "cursor-pointer hover:bg-macaw/5"
					)}
				>
					<span className="opacity-30">___</span>
				</button>
			)}
		</span>
	)
}

// ============================================================================
// Main Gap Match Interaction Component
// ============================================================================

export interface GapMatchInteractionProps {
	interaction: DisplayGapMatchInteraction
	response?: string[]
	onAnswerSelect?: (value: string[]) => void
	disabled?: boolean
	hasSubmitted?: boolean
	/** Per-gap correctness: Record<gapId, isCorrect> */
	gapCorrectness?: Record<string, boolean>
}

export function GapMatchInteraction({
	interaction,
	response = [],
	onAnswerSelect,
	disabled = false,
	hasSubmitted = false,
	gapCorrectness
}: GapMatchInteractionProps) {
	// Convert QTI response to internal state
	const currentPairs = React.useMemo(() => fromQtiResponse(response), [response])

	// Track selected source token for click-to-place
	const [selectedSourceId, setSelectedSourceId] = React.useState<string | null>(null)

	// Track active drag for overlay
	const [activeDragId, setActiveDragId] = React.useState<string | null>(null)
	const activeSource = React.useMemo(
		() => interaction.gapTexts.find((gt) => gt.id === activeDragId),
		[activeDragId, interaction.gapTexts]
	)

	// Calculate remaining uses for each source token
	const getRemainingUses = (sourceId: string): number => {
		const source = interaction.gapTexts.find((gt) => gt.id === sourceId)
		if (!source) return 0
		if (source.matchMax === 0) return 999 // Unlimited
		const usedCount = Object.values(currentPairs).filter((id) => id === sourceId).length
		return source.matchMax - usedCount
	}

	// Handle source token click (select for click-to-place)
	const handleSourceClick = (sourceId: string) => {
		if (disabled || hasSubmitted) return
		setSelectedSourceId((prev) => (prev === sourceId ? null : sourceId))
	}

	// Handle gap click (place selected token)
	const handleGapClick = (gapId: string) => {
		if (disabled || hasSubmitted || !selectedSourceId) return

		// Check if source still has remaining uses
		if (getRemainingUses(selectedSourceId) <= 0) {
			setSelectedSourceId(null)
			return
		}

		// Place the selected token in the gap
		const newPairs = { ...currentPairs, [gapId]: selectedSourceId }
		onAnswerSelect?.(toQtiResponse(newPairs))
		setSelectedSourceId(null)
	}

	// Handle clearing a gap
	const handleClearGap = (gapId: string) => {
		if (disabled || hasSubmitted) return
		const newPairs = { ...currentPairs }
		delete newPairs[gapId]
		onAnswerSelect?.(toQtiResponse(newPairs))
	}

	// Handle drag end (drop source on gap)
	const handleDragEnd = (event: DragEndEvent) => {
		setActiveDragId(null)
		const { active, over } = event
		if (!over) return

		const sourceData = active.data.current
		const targetData = over.data.current

		if (sourceData?.type === "source" && targetData?.type === "gap") {
			const sourceId = sourceData.sourceId
			const gapId = targetData.gapId

			if (typeof sourceId === "string" && typeof gapId === "string") {
				// Check remaining uses
				if (getRemainingUses(sourceId) <= 0) return

				const newPairs = { ...currentPairs, [gapId]: sourceId }
				onAnswerSelect?.(toQtiResponse(newPairs))
			}
		}
	}

	// Build gap embeds for HTMLContent
	const gapEmbeds = React.useMemo(() => {
		const embeds: Record<string, DisplayGap> = {}
		for (const gap of interaction.gaps) {
			embeds[gap.id] = gap
		}
		return embeds
	}, [interaction.gaps])

	return (
		<DndContext
			collisionDetection={closestCenter}
			onDragStart={(e) => {
				const id = e.active.id
				const sourceId = typeof id === "string" ? id.replace("source-", "") : null
				setActiveDragId(sourceId)
				setSelectedSourceId(null) // Clear selection when dragging
			}}
			onDragEnd={handleDragEnd}
		>
			<div className="space-y-6">
				{/* Source Pool */}
				<div
					className={cn(
						"p-4 rounded-xl border-2 border-dashed",
						"flex flex-wrap gap-2 min-h-[3.5rem]",
						disabled || hasSubmitted ? "bg-muted/30 border-muted" : "bg-muted/10 border-muted-foreground/20"
					)}
				>
					{interaction.gapTexts.map((source) => (
						<SourceToken
							key={source.id}
							id={source.id}
							contentHtml={source.contentHtml}
							disabled={disabled || hasSubmitted}
							isSelected={selectedSourceId === source.id}
							onClick={() => handleSourceClick(source.id)}
							remainingUses={getRemainingUses(source.id)}
						/>
					))}
					{interaction.gapTexts.every((s) => getRemainingUses(s.id) <= 0)}
				</div>

				{/* Hint for click-to-place */}
				{selectedSourceId && !disabled && !hasSubmitted && (
					<p className="text-sm text-muted-foreground animate-pulse">Click a gap to place the selected token</p>
				)}

				{/* Content with Gap Slots */}
				<div className="leading-relaxed text-lg">
					<HTMLContent
						html={interaction.contentHtml}
						gapEmbeds={gapEmbeds}
						renderGap={(gap) => {
							const sourceId = currentPairs[gap.id]
							const source = interaction.gapTexts.find((gt) => gt.id === sourceId)

							return (
								<GapSlot
									gapId={gap.id}
									filledWith={source}
									onClear={() => handleClearGap(gap.id)}
									onClick={() => handleGapClick(gap.id)}
									disabled={disabled || hasSubmitted}
									isCorrect={gapCorrectness?.[gap.id]}
									hasSubmitted={hasSubmitted}
								/>
							)
						}}
					/>
				</div>
			</div>

			{/* Drag Overlay - dropAnimation={null} prevents the slow settling lag */}
			<DragOverlay dropAnimation={null}>
				{activeSource ? (
					<div className={cn(sourceTokenVariants({ state: "selected" }), "shadow-xl cursor-grabbing")}>
						<SafeInlineHTML html={activeSource.contentHtml} />
					</div>
				) : null}
			</DragOverlay>
		</DndContext>
	)
}

export default GapMatchInteraction
